// ============================================================
// src/contexts/AuthContext.tsx - Authentication Context
// ============================================================
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authApi } from '@/api';
import { APP_CONSTANTS, PERMISSIONS, ROLES, type PermissionKey } from '@/constants';
import { StorageUtil } from '@/utils/storage/storageUtil';
import type { UserProfile } from '@/types/auth';
import { loginRequest as msalLoginRequest } from '@/config/msalConfig';
import { ENV } from '@/config/env';

// ── Types ─────────────────────────────────────────────────────────────────
interface AuthContextState {
  user: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isManager: boolean;
  loginWithSSO: () => Promise<void>;
  logout: () => void;
  hasRole: (role: ROLES) => boolean;
  hasAnyRole: (roles: ROLES[]) => boolean;
  hasPermission: (permission: PermissionKey) => boolean;
  refreshUser: () => Promise<void>;
  setActiveOrganization: (organizationId: string) => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────
const AuthContext = createContext<AuthContextState>({} as AuthContextState);

// ── Provider ──────────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const { instance: msalInstance } = useMsal();

  // ── Initialize from storage ─────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      const storedToken = StorageUtil.get<string>(APP_CONSTANTS.TOKEN_KEY);
      if (storedToken) {
        setToken(storedToken);
        try {
          const { data } = await authApi.me();
          setUser(data);
        } catch {
          clearAuth();
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  // ── Clear auth state ────────────────────────────────────────────────
  const clearAuth = useCallback(() => {
    StorageUtil.remove(APP_CONSTANTS.TOKEN_KEY);
    StorageUtil.remove(APP_CONSTANTS.REFRESH_TOKEN_KEY);
    StorageUtil.remove(APP_CONSTANTS.USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // ── Email + Password Login (disabled - Microsoft SSO only) ────────────
  // const login = useCallback(async (credentials: LoginRequest) => {
  //   const { data } = await authApi.login(credentials);
  //   StorageUtil.set(APP_CONSTANTS.TOKEN_KEY, data.token);
  //   StorageUtil.set(APP_CONSTANTS.REFRESH_TOKEN_KEY, data.refreshToken);
  //   StorageUtil.set(APP_CONSTANTS.USER_KEY, data.employee);
  //   setToken(data.token);
  //   setUser(data.employee);
  // }, []);

  // ── Microsoft SSO Login ─────────────────────────────────────────────
  const loginWithSSO = useCallback(async () => {
    // Guard: if the MSAL client ID is missing (e.g. env/.env.development.local not loaded),
    // surface a clear error immediately rather than letting the popup silently fail.
    const clientId = ENV.MSAL_CLIENT_ID;
    if (!clientId || clientId === 'undefined' || clientId.trim() === '') {
      console.error('MSAL clientId missing - check frontend/env/.env.development.local for VITE_MSAL_CLIENT_ID');
      toast.error('SSO not configured. Contact administrator.');
      return;
    }

    try {
      const msalResponse = await msalInstance.loginPopup(msalLoginRequest);
      
      // Set the active account in MSAL instance
      if (msalResponse.account) {
        msalInstance.setActiveAccount(msalResponse.account);
        console.log('✅ MSAL account set as active:', msalResponse.account.username);
      }
      
      const msalToken = msalResponse.accessToken;
      const idToken = msalResponse.idToken;
      const email =
        msalResponse.account?.username ||
        (msalResponse.account?.idTokenClaims as Record<string, string> | undefined)?.preferred_username ||
        '';

      const { data } = await authApi.ssoLogin(msalToken, email as string, idToken);
      StorageUtil.set(APP_CONSTANTS.TOKEN_KEY, data.token);
      StorageUtil.set(APP_CONSTANTS.REFRESH_TOKEN_KEY, data.refreshToken);
      StorageUtil.set(APP_CONSTANTS.USER_KEY, data.employee);
      setToken(data.token);
      setUser(data.employee);
    } catch (error) {
      console.error('SSO error detail:', error);
      throw error;
    }
  }, [msalInstance]);

  // ── Logout ──────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearAuth();
    try {
      msalInstance.logoutPopup();
    } catch {
      // MSAL logout may fail if not SSO session - ignore
    }
  }, [clearAuth, msalInstance]);

  // ── Role & Permission Checks ────────────────────────────────────────
  const hasRole = useCallback(
    (role: ROLES) => user?.role === role,
    [user],
  );

  const hasAnyRole = useCallback(
    (roles: ROLES[]) => roles.includes(user?.role as ROLES),
    [user],
  );

  const hasPermission = useCallback(
    (permission: PermissionKey) => {
      if (!user) return false;
      const allowedRoles = PERMISSIONS[permission];
      return (allowedRoles as readonly ROLES[]).includes(user.role as ROLES);
    },
    [user],
  );

  const storedUser = StorageUtil.get<UserProfile>(APP_CONSTANTS.USER_KEY);
  const isManager = Boolean(user?.isManager ?? storedUser?.isManager);

  // ── Refresh user data ───────────────────────────────────────────────
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
      StorageUtil.set(APP_CONSTANTS.USER_KEY, data);
    } catch {
      clearAuth();
    }
  }, [clearAuth]);

  const setActiveOrganization = useCallback(async (organizationId: string) => {
    const { data } = await authApi.setActiveOrganization(organizationId);
    StorageUtil.set(APP_CONSTANTS.TOKEN_KEY, data.token);
    StorageUtil.set(APP_CONSTANTS.REFRESH_TOKEN_KEY, data.refreshToken);
    StorageUtil.set(APP_CONSTANTS.USER_KEY, data.employee);
    queryClient.clear();
    setToken(data.token);
    setUser(data.employee);
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        isManager,
        loginWithSSO,
        logout,
        hasRole,
        hasAnyRole,
        hasPermission,
        refreshUser,
        setActiveOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
